import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { PenyuluhProvinsiDataType } from './types'
import type {
  FilterPenyuluhProvinsiValues,
  MutationPenyuluhProvinsiValues
} from './validations'

import { penyuluh } from './penyuluh'

const PROVINSI_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['penyuluh-provinsi', key] as const

const getAll = (
  params?: FilterPenyuluhProvinsiValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PenyuluhProvinsiDataType>>
> => ({
  queryKey: PROVINSI_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PenyuluhProvinsiDataType>>
      >('/kelembagaan/dinas', {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string
): ServiceQueryOptionType<
  ServiceGetByIdResponse<PenyuluhProvinsiDataType>
> => ({
  queryKey: PROVINSI_QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetByIdResponse<PenyuluhProvinsiDataType>
      >(`/kelembagaan/dinas/${id}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PenyuluhProvinsiDataType>,
  MutationPenyuluhProvinsiValues
> => ({
  mutationFn: async (data: MutationPenyuluhProvinsiValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<PenyuluhProvinsiDataType>
      >('/kelembagaan/dinas', {
        ...data,
        id_poktan: undefined,
        id_desa: data.id_desa.value,
        id_kecamatan: data.district_id.value,
        latitude: data.latitude?.toString(),
        longitude: data.longitude?.toString()
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PenyuluhProvinsiDataType>,
  MutationPenyuluhProvinsiValues
> => ({
  mutationFn: async (data: MutationPenyuluhProvinsiValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<PenyuluhProvinsiDataType>
      >(`/kelembagaan/dinas/${data.id_penyuluh_provinsi}`, {
        ...data,
        id_poktan: undefined,
        id_desa: data.id_desa.value,
        id_kecamatan: data.district_id.value,
        latitude: data.latitude?.toString(),
        longitude: data.longitude?.toString()
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PenyuluhProvinsiDataType>,
  { id_penyuluh_provinsi: string }
> => ({
  mutationFn: async (data: { id_penyuluh_provinsi: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PenyuluhProvinsiDataType>
      >(`/kelembagaan/dinas/${data.id_penyuluh_provinsi}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const exportData = (params?: FilterPenyuluhProvinsiValues) => ({
  mutationFn: async () => {
    try {
      const response = await api.get<{
        status: string
        url?: string | null
        job_id?: string
        message?: string
        progress?: number
      }>(`/kelembagaan/dinas/export`, { params })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const penyuluhProvinsi = {
  queryKey: [PROVINSI_QUERY_KEY(undefined)[0]],
  getAll,
  create,
  getById,
  update,
  deleteData,
  exportData,
  detail: {
    penyuluh
  }
}
